import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'default';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return context;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => {
    if (confirmState) {
      confirmState.resolve(true);
      setConfirmState(null);
    }
  };

  const handleCancel = () => {
    if (confirmState) {
      confirmState.resolve(false);
      setConfirmState(null);
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      
      {/* DaisyUI Modal */}
      {confirmState?.isOpen && (
        <div className="modal modal-open z-[9999]" onClick={handleCancel}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="modal-box relative card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Icon */}
            <div className={`flex items-center gap-3 mb-4 p-4 -mx-6 -mt-6 ${
              confirmState.options.type === 'danger' 
                ? 'bg-error/10' 
                : 'bg-primary/10'
            }`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                confirmState.options.type === 'danger'
                  ? 'bg-error text-white'
                  : 'bg-primary/20 text-primary'
              }`}>
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-xl">
                {confirmState.options.title || 'Confirm Action'}
              </h3>
            </div>

            {/* Content */}
            <p className="py-4 text-base-content/70">
              {confirmState.options.message}
            </p>

            {/* Actions */}
            <div className="modal-action">
              <button
                onClick={handleCancel}
                className="btn btn-ghost"
              >
                {confirmState.options.cancelText || 'Cancel'}
              </button>
              <button
                onClick={handleConfirm}
                className={`btn ${
                  confirmState.options.type === 'danger'
                    ? 'btn-error'
                    : 'btn-primary'
                }`}
              >
                {confirmState.options.confirmText || 'Confirm'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}


